import { TestingCoverageDimension, TestingCoverageItem } from "@common/models/Analytics.model";

/**
 * Flattens a coverage data source into a `{ [dimension]: { score?, items } }` map.
 *
 * The `tickets` source is a flat map of dimension → items, while the `sessions`
 * source splits into `{ dimensions, metadata }` where each entry is `{ score, items }`.
 * This normalizes both: it reads either the nested `dimensions` object or the flat
 * source itself, accepting values that are either a plain items array (tickets, or
 * legacy metadata) or a `{ score, items }` object (new dimensions/metadata), then
 * merges any `metadata` dimensions on top.
 */
export function getDimensionMap(source: unknown): Record<string, TestingCoverageDimension> {
  const result: Record<string, TestingCoverageDimension> = {};
  if (!source || typeof source !== "object") return result;

  const record = source as Record<string, unknown>;
  const dimensions = (record.dimensions as Record<string, unknown> | undefined) ?? record;

  const assign = (entries: Record<string, unknown>) => {
    for (const [key, value] of Object.entries(entries)) {
      if (Array.isArray(value)) {
        result[key] = { items: value as TestingCoverageItem[] };
      } else if (value && typeof value === "object" && Array.isArray((value as { items?: unknown }).items)) {
        const dimension = value as TestingCoverageDimension;
        result[key] = { score: dimension.score, items: dimension.items };
      }
    }
  };

  assign(dimensions);

  const metadata = record.metadata as Record<string, unknown> | undefined;
  if (metadata && typeof metadata === "object") assign(metadata);

  return result;
}
